import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/useAuthStore'; // 🟢 CORRECCIÓN: Importamos el store de Zustand
import PostActionsMenu from './PostActionsMenu'; // Importamos el menú de acciones
import PremiumContentBlocker from './PremiumContentBlocker'; // 🟢 Importamos blocker premium
import toast from 'react-hot-toast';
import { reactionsConfig } from '../../components/posts/reactionsConfig';
import ReactionButton from './ReactionButton';
import QuotedPost from './QuotedPost';
import { getMediaUrl, DEFAULT_MEDIA_URL, DEFAULT_AVATAR_URL, withImageFallback } from '../../utils/imageUtils';
const avatarFallback = '/images/avatar/DefaultProfile.jpg';

// Helper para formatear la fecha
const formatTimeAgo = (date) => {
  if (!date) return '';
  const seconds = Math.floor((new Date() - new Date(date)) / 1000);
  let interval = seconds / 31536000;
  if (interval > 1) return `hace ${Math.floor(interval)} años`;
  interval = seconds / 2592000;
  if (interval > 1) return `hace ${Math.floor(interval)} meses`;
  interval = seconds / 86400;
  if (interval > 1) return `hace ${Math.floor(interval)} días`;
  interval = seconds / 3600;
  if (interval > 1) return `hace ${Math.floor(interval)} horas`;
  interval = seconds / 60;
  if (interval > 1) return `hace ${Math.floor(interval)} min`;
  return 'hace unos segundos';
};

// 🟢 NUEVO: Componente para el icono de privacidad
const PrivacyIcon = ({ privacy }) => {
  const iconMap = {
    public: { icon: 'fa-globe-americas', title: 'Público' },
    friends: { icon: 'fa-user-friends', title: 'Amigos' },
    private: { icon: 'fa-lock', title: 'Solo yo' },
  };

  const { icon, title } = iconMap[privacy] || iconMap.public;

  return (
    <div
      className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
      title={title}
    >
      <i className={`fas ${icon}`}></i>
    </div>
  );
};

const PostCard = ({
  post,
  onLike,
  openModal,
  isOwner,
  onUpdatePrivacy,
  onDeletePost,
  onQuotePost,
  onForwardPost,
  onEditPost,
  badgeLabel,
}) => {
  const currentUser = useAuthStore((state) => state.currentUser);
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn);
  const hasPassFor = useAuthStore((state) => state.hasPassFor); // 🟢 Obtener función de validación
  const navigate = useNavigate();
  const authorIsSenator = post?.author?.rank === 'senator' || post?.author?.tier === 'senador';

  // 🟢 Verificar si es contenido premium
  const isPremium = post.privacy === 'premium' || post.isPremium;
  const hasPass = hasPassFor(post.author?._id);
  const isContentBlocked = isPremium && !hasPass && post.author?._id !== currentUser?._id;

  const currentUserLike = post.likes.find(
    (like) => like.user && (like.user._id || like.user) === currentUser?._id
  );

  const handleCommentClick = () => {
    if (!isLoggedIn) {
      toast.error('Necesitas iniciar sesión para comentar.');
      navigate('/auth');
      return;
    }
    openModal(post);
  };

  const handleShareClick = () => {
    const postUrl = `${window.location.origin}/post/${post._id}`;
    navigator.clipboard.writeText(postUrl);
    toast.success('¡Enlace copiado al portapapeles!');
  };

  if (!post || !post.author) {
    return null; // Evita errores si el post o el autor no están definidos
  }

  // 🟢 SOLUCIÓN: Si el post es un artículo, lo renderizamos de forma especial.
  if (post.type === 'article') {
    return (
      <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden group transition-colors duration-300">
        {/* Imagen de Portada del Artículo */}
        {post.images && post.images.length > 0 && (
          <div className="relative overflow-hidden">
            <Link to={`/post/${post._id}`}>
              <img
                src={getMediaUrl(post.images[0], DEFAULT_MEDIA_URL)}
                alt={post.title}
                className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
                onError={(event) => withImageFallback(event, DEFAULT_MEDIA_URL)}
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center">
                <i className="fas fa-eye text-blue-500 text-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></i>
              </div>
            </Link>
            {post.category && (
              <Link
                to={`/news/${post.category.slug}`}
                className="absolute top-3 right-3 bg-blue-600 text-white text-xs font-bold py-1 px-3 rounded-full hover:bg-blue-700 transition-colors z-10"
              >
                {post.category.name}
              </Link>
            )}
          </div>
        )}
          <div className="p-4">
          {/* Cabecera con autor y categoría */}
          <div className="flex items-center mb-2">
            <Link to={`/profile/${post.author.username}`}>
              <div className="relative mr-2">
                {authorIsSenator && (
                  <div className="absolute -inset-0.5 rounded-full bg-gradient-to-r from-amber-300 via-yellow-500 to-orange-500 animate-spin-slow"></div>
                )}
                {post.author?.tier === 'patricio' && (
                  <div className="absolute -inset-0.5 rounded-full bg-[#b87333]"></div>
                )}
                <img
                  src={post.author.avatar ? getMediaUrl(post.author.avatar, DEFAULT_AVATAR_URL) : avatarFallback}
                  alt={post.author.username}
                  className={`relative w-8 h-8 rounded-full object-cover border-2 border-white dark:border-gray-700 ${authorIsSenator ? 'gold-aura' : ''}`}
                  onError={(event) => withImageFallback(event, DEFAULT_AVATAR_URL)}
                />
              </div>
            </Link>
            <div>
              <Link
                to={`/profile/${post.author.username}`}
                className="relative flex font-semibold text-sm text-gray-800 dark:text-gray-100 hover:underline"
              >
                {post.author.username}
              </Link>
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <span>{formatTimeAgo(post.createdAt)}</span>
                {badgeLabel && <span className="uppercase tracking-wide">{badgeLabel}</span>}
              </div>
            </div>
          </div>
          {/* Título y Snippet */}
          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">{post.title}</h3>
          <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">{post.snippet}</p>

          {/* Botón para leer más */}
          <Link
            to={`/post/${post._id}`}
            className="font-semibold text-blue-600 hover:text-blue-800 text-sm"
          >
            Leer más <i className="fas fa-arrow-right ml-1"></i>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-4 relative transition-colors duration-300">
      <div className="absolute top-4 right-4">
        {isOwner ? (
          <PostActionsMenu
            post={post}
            onUpdatePrivacy={onUpdatePrivacy}
            onDeletePost={onDeletePost}
            onQuotePost={onQuotePost}
            onForwardPost={onForwardPost}
            onEditPost={onEditPost}
          />
        ) : (
          <PrivacyIcon privacy={post.privacy} />
        )}
      </div>

      {/* Cabecera del Post */}
      <div className="flex items-center mb-4">
        <Link to={`/profile/${post.author.username}`}>
          <div className="relative mr-3">
            {authorIsSenator && (
              <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-amber-300 via-yellow-500 to-orange-500 animate-spin-slow"></div>
            )}
            {post.author?.tier === 'patricio' && (
              <div className="absolute -inset-1 rounded-full bg-[#b87333]"></div>
            )}
            <img
              src={post.author.avatar ? getMediaUrl(post.author.avatar, DEFAULT_AVATAR_URL) : avatarFallback}
              alt={post.author.username}
              className={`relative w-12 h-12 rounded-full object-cover border-4 border-white dark:border-gray-700 ${authorIsSenator ? 'gold-aura' : ''}`}
              onError={(event) => withImageFallback(event, DEFAULT_AVATAR_URL)}
            />
          </div>
        </Link>
        <div>
          <Link
            to={`/profile/${post.author.username}`}
            className="font-bold text-gray-800 dark:text-gray-100 hover:underline"
          >
            {post.author.username}
          </Link>
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span>{formatTimeAgo(post.createdAt)}</span>
            {badgeLabel && <span className="uppercase tracking-wide">{badgeLabel}</span>}
          </div>
        </div>
      </div>

      {/* Contenido del Post */}
      {post.content && <p className="text-gray-800 dark:text-gray-200 mb-4">{post.content}</p>}

      {/* 🟢 RENDERIZADO DEL POST CITADO (REPOST) */}
      {post.originalPost && <QuotedPost post={post.originalPost} />}

      {/* 🟢 BLOQUEADOR DE CONTENIDO PREMIUM */}
      {isContentBlocked ? (
        <PremiumContentBlocker post={post} isBlocked={true} />
      ) : (
        <>
          {/* Imagen del Post */}
          {post.images && post.images.length > 0 && (
            <div className="mb-4 rounded-lg overflow-hidden max-h-[550px]">
              <img
                src={getMediaUrl(post.images[0], DEFAULT_MEDIA_URL)}
                alt="Contenido del post"
                className="w-full h-full max-h-[550px] object-cover"
                onError={(event) => withImageFallback(event, DEFAULT_MEDIA_URL)}
              />
            </div>
          )}

          {/* Video del Post */}
          {post.videos && post.videos.length > 0 && (
            <div className="mb-4 rounded-lg overflow-hidden max-h-[550px]">
              <video controls className="w-full h-full max-h-[550px] object-cover">
                <source src={getMediaUrl(post.videos[0])} type="video/mp4" />
                Tu navegador no soporta la etiqueta de video.
              </video>
            </div>
          )}
        </>
      )}

      {/* 🟢 INICIO: Bloque para mostrar información del producto */}
      {post.type === 'product' && post.price && (
        <div className="px-4 pt-4 pb-2 border-t border-gray-200 dark:border-gray-700 mt-4">
          <div className="flex justify-between items-center">
            <p className="text-2xl font-bold text-green-600">
              {new Intl.NumberFormat('es-US', { style: 'currency', currency: post.currency || 'USD' }).format(post.price)}
            </p>
            {post.externalLink && (
              <a href={post.externalLink} target="_blank" rel="noopener noreferrer" className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg transition-colors">
                Contactar / Comprar
              </a>
            )}
          </div>
        </div>
      )}
      {/* 🟢 FIN: Bloque para mostrar información del producto */}

      {/* Estadísticas (Likes y Comentarios) */}
      <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400 mb-2">
        <div className="flex items-center">
          {post.likes.length > 0 && (
            <>
              <div className="flex -space-x-2 mr-2">
                {post.likes.slice(0, 8).map(
                  (
                    like,
                    index // 🟢 LÍMITE: Mostramos hasta 8 avatares.
                  ) =>
                    like.user && (
                      <img
                        key={index}
                        className="w-5 h-5 rounded-full border-2 border-white dark:border-gray-700"
                        src={like.user.avatar ? getMediaUrl(like.user.avatar, DEFAULT_AVATAR_URL) : avatarFallback}
                        alt={like.user.username}
                        title={like.user.username}
                        onError={(event) => withImageFallback(event, DEFAULT_AVATAR_URL)}
                      />
                    )
                )}
              </div>
              <span className="text-xs">
                {post.likes.length}{' '}
                {post.likes.length === 1 ? 'reacción' : 'reacciones'}
              </span>
            </>
          )}
        </div>
        {/* 🟢 SOLUCIÓN: Mostramos las vistas en lugar de los comentarios */}
        {post.views > 0 && (
          <span className="text-xs flex items-center">
            <i className="fas fa-eye mr-1"></i> {post.views}
          </span>
        )}
      </div>

      {/* Acciones (Like y Comentar) */}
      <div className="flex space-x-3">
        <ReactionButton
          onLike={() =>
            onLike(post._id, currentUserLike ? currentUserLike.type : 'like')
          }
          onSelectReaction={(reactionType) => onLike(post._id, reactionType)}
          currentUserLike={currentUserLike}
        />
        <button
          onClick={handleCommentClick}
          className="px-2 py-2 rounded-lg text-center font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <i className="fas fa-comment-alt mr-1"></i>
          <span className="mr-1">Comentar</span>
          {/* 🟢 SOLUCIÓN: El contador de comentarios ahora está dentro del botón */}
          {post.comments.length > 0 && (
            <span className="text-blue-500 font-bold">
              {post.comments.length}
            </span>
          )}
        </button>
        <button
          className="px-2 py-2 rounded-lg text-center font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          onClick={handleShareClick}
          title="Compartir"
        >
          <i className="fas fa-share-square mr-2"></i>
          Compartir
        </button>
      </div>
    </div>
  );
};

export default PostCard;
